import { useMemo, useState } from "react";

import type {
  RepairCandidate,
  RepairSessionStatus
} from "../../api/statelessClient";
import { isRepairNoResultStatus } from "../../api/statelessClient";
import { RepairCandidateTimetablePreview } from "./RepairCandidateTimetablePreview";

export function RepairSessionPanel({
  session,
  accessToken,
  busy,
  error,
  onClose,
  onCheckConflicts,
  onRetry,
  onReject,
  onAccept
}: {
  session: RepairSessionStatus | null;
  accessToken: string;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onCheckConflicts: () => void;
  onRetry: () => void;
  onReject: (lockKeys: string[]) => void;
  onAccept: () => void;
}): React.JSX.Element {
  const round = session?.current_round ?? null;
  const candidate = round?.candidate ?? null;
  const [rejecting, setRejecting] = useState(false);
  const [selectedLocks, setSelectedLocks] = useState<string[]>([]);
  const counts = candidateCounts(candidate);
  const pending = !round || round.status === "PENDING" || round.status === "RUNNING";
  const noResult = isRepairNoResultStatus(round?.status);
  const cancelled = round?.status === "CANCELLED";
  const fullCandidate = candidate?.completeness === "COMPLETE";
  const partialCandidate = candidate?.completeness === "INCOMPLETE";
  const locks = useMemo(
    () => session?.locked_repairs ?? [],
    [session?.locked_repairs]
  );

  return (
    <>
      {locks.length > 0 ? (
        <details className="repair-locked-conditions">
          <summary>已锁定 {locks.length} 类不能改动的条件</summary>
          <ul>
            {locks.map((item, index) => (
              <li key={String(item.lock_key ?? index)}>
                {String(item.title ?? "已锁定的调整条件")}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {pending ? (
        <div className="repair-running" role="status" aria-live="polite">
          <p>正在寻找改动较少的可行调整方案，请稍候…</p>
          <div className="progress-track" aria-label="调整方案求解中"><span /></div>
          <small>本轮最多运行 60 秒；关闭窗口后任务仍会继续。</small>
        </div>
      ) : null}

      {noResult ? (
        <div className="scheduling-feedback" role="alert">
          <h3>本轮没有找到可行的解决方案</h3>
          <p>在本轮允许调整的范围内不存在完整课表。可以检查当前排课条件，查找能够明确说明的冲突。</p>
        </div>
      ) : null}

      {cancelled ? (
        <p className="error-message" role="alert">
          {round?.error?.message ?? "本轮任务已取消。"}
        </p>
      ) : null}

      {candidate ? (
        <>
          <div className={`candidate-completion${partialCandidate ? " incomplete" : ""}`}>
            <strong>
              {fullCandidate
                ? `已安排全部 ${counts.scheduled} / ${counts.required} 节课程`
                : `最大可排参考方案：已排 ${counts.scheduled} / ${counts.required} 节`}
            </strong>
            <span>
              {fullCandidate
                ? "以下调整均来自本轮实际课表；采纳后会直接保存为正式版本。"
                : "这是一份安全但未排完整的参考课表，可以保存，也可以继续寻找完整方案。"}
            </span>
          </div>
          <section className="candidate-relaxations" aria-labelledby="repair-summary-title">
            <h3 id="repair-summary-title">
              本方案实际调整了 {candidate.repair_items.length} 项条件
            </h3>
            {candidate.repair_items.length ? (
              <ul>
                {candidate.repair_items.map((item) => (
                  <li key={item.repair_id}>
                    {rejecting && fullCandidate ? (
                      <label className="repair-lock-choice">
                        <input
                          type="checkbox"
                          checked={selectedLocks.includes(item.lock_key)}
                          onChange={(event) =>
                            setSelectedLocks((current) =>
                              event.target.checked
                                ? [...new Set([...current, item.lock_key])]
                                : current.filter((key) => key !== item.lock_key)
                            )
                          }
                        />
                        <span>
                          <strong>不能接受：{item.title}</strong>
                          <small>锁定后，后续方案不能再改动这一类条件。</small>
                        </span>
                      </label>
                    ) : (
                      <>
                        <strong>{item.title}</strong>
                        <span>原要求：{item.original_value}</span>
                        <span>本方案：{item.proposed_value}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>没有放宽可调整条件。</p>
            )}
          </section>
          {partialCandidate ? <UnassignedSummary candidate={candidate} /> : null}
          <details className="candidate-timetable-details" open>
            <summary>查看{partialCandidate ? "最大可排参考" : "完整"}课表</summary>
            <RepairCandidateTimetablePreview
              sessionId={session?.session_id ?? ""}
              roundId={round?.round_id ?? ""}
              accessToken={accessToken}
            />
          </details>
        </>
      ) : null}

      {error ? (
        <p className="error-message" role="alert">
          {error instanceof Error ? error.message : "调整方案操作失败，请重试"}
        </p>
      ) : null}

      <div className="modal-actions candidate-actions">
        <button className="secondary-button" type="button" disabled={busy} onClick={onClose}>
          关闭，稍后再看
        </button>
        {noResult ? (
          <button
            className="primary-button"
            type="button"
            disabled={busy}
            onClick={onCheckConflicts}
          >
            {busy ? "正在发起检查…" : "检查冲突条件"}
          </button>
        ) : null}
        {fullCandidate && !rejecting ? (
          <>
            <button
              className="secondary-button"
              type="button"
              disabled={busy}
              onClick={() => {
                setSelectedLocks([]);
                setRejecting(true);
              }}
            >
              不能接受
            </button>
            <button className="primary-button" type="button" disabled={busy} onClick={onAccept}>
              {busy ? "正在保存…" : "采纳并保存课表"}
            </button>
          </>
        ) : null}
        {fullCandidate && rejecting ? (
          <>
            <button
              className="secondary-button"
              type="button"
              disabled={busy}
              onClick={() => setRejecting(false)}
            >
              返回方案
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={busy || selectedLocks.length === 0}
              onClick={() => onReject(selectedLocks)}
            >
              锁定所选条件并重新求解
            </button>
          </>
        ) : null}
        {partialCandidate ? (
          <>
            <button className="secondary-button" type="button" disabled={busy} onClick={onRetry}>
              继续寻找完整方案
            </button>
            <button className="primary-button" type="button" disabled={busy} onClick={onAccept}>
              {busy ? "正在保存…" : "应用这份参考方案"}
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}

function UnassignedSummary({ candidate }: { candidate: RepairCandidate }): React.JSX.Element {
  const rows = candidate.unassigned ?? [];
  return (
    <section className="repair-unassigned" aria-labelledby="unassigned-title">
      <h3 id="unassigned-title">仍未排入的课程与阻塞因素</h3>
      {rows.length === 0 ? (
        <p>没有可展示的未排课程。</p>
      ) : (
        <ul>
          {rows.map((row, index) => (
            <li key={String(row.plan_id ?? index)}>
              <strong>
                {String(row.class_name ?? "未命名班级")} · {String(row.course_name ?? "未命名课程")}
              </strong>
              <span>
                已排 {numberValue(row.scheduled_lessons)} / {numberValue(row.required_lessons)} 节，
                仍有 {numberValue(row.unassigned_lessons)} 节未排
              </span>
              {Array.isArray(row.blockers) ? (
                <ul>
                  {row.blockers.map((blocker, blockerIndex) => (
                    <li key={blockerIndex}>
                      {isRecord(blocker) ? String(blocker.message ?? "多项条件共同影响") : "多项条件共同影响"}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function candidateCounts(candidate: RepairCandidate | null): {
  scheduled: number;
  required: number;
} {
  if (!candidate) return { scheduled: 0, required: 0 };
  const required = candidate.plan_decisions.reduce(
    (sum, row) => sum + numberValue(row.weekly_lessons),
    0
  );
  return { scheduled: candidate.assignments.length, required };
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
